import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { formatErrorList } from '../../shared/alert/format-error-list';
import { TranslateService } from '@ngx-translate/core';
import { HttpResponse } from '@angular/common/http';
import { Principal } from '../../shared';
import { MANAGE_LOT_PERMISSIONS } from '../../shared/auth/permissions';
import { AlertService } from '../../shared/alert/alert.service';
import { JhiEventManager } from '../../shared/core-utils';
import { TransactionService } from '../../shared/inventory/service/transaction.service';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';

const SUCCESS_CONFIRMED = 'inventoryManagerApp.lot.deposit.success.confirmed.transaction';
const SUCCESS_PENDING = 'inventoryManagerApp.lot.deposit.success.pending.transaction';

@Component({
    selector: 'jhi-lot-import-deposit-mapping',
    templateUrl: './lot-import-deposit-mapping.dialog.component.html'
})

export class LotImportDepositMappingDialogComponent implements OnInit, OnDestroy {

    @Input() importData: Array<Array<any>>;
    header: Array<string>;
    lotUIDMapping;
    amountMapping;
    notesMapping;
    confirmDepositTransactions;
    CONFIRM_DEPOSIT_INVENTORY_PERMISSION = [...MANAGE_LOT_PERMISSIONS, 'DEPOSIT_INVENTORY', 'CREATE_CONFIRMED_DEPOSITS'];
    subscriptionsManager = new SubscriptionsManager();

    constructor(private alertService: AlertService,
                private modal: NgbActiveModal,
                private transactionService: TransactionService,
                private eventManager: JhiEventManager,
                private translateService: TranslateService,
                private principal: Principal) {
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.header = this.importData[0];
        this.lotUIDMapping = this.mappingHeader(this.importData[0], 'Lot UID');
        this.amountMapping = this.mappingHeader(this.importData[0], 'Amount');
        this.notesMapping = this.mappingHeader(this.importData[0], 'Notes');

        this.principal.hasAnyAuthority(this.CONFIRM_DEPOSIT_INVENTORY_PERMISSION).then((response) => {
            this.confirmDepositTransactions = { selected: response };
        });
    }

    mappingHeader(header: Array<string>, mapping: string) {
        for (const column of header) {
            if (column.toLowerCase() === mapping.toLowerCase()) {
                return column;
            }
        }
        return '';
    }

    back() {
        this.modal.dismiss();
    }

    closed() {
        this.modal.dismiss();
    }

    proceed() {
        if (this.validate()) {
            const lotDeposits = this.importData
                .slice(1) // skip header
                .filter((row) => row.some((column) => Boolean(column.trim()))) // remove empty rows
                .map((row) => {
                    return Object.assign({
                        lotUID: row[this.getLotUIDColumnIndex()],
                        amount: row[this.getAmountColumnIndex()],
                        notes: row[this.getNotesColumnIndex()]
                    });
                });
            const lotUUIDs = lotDeposits.map((lotDeposit) => lotDeposit.lotUID);
            if (this.confirmDepositTransactions.selected) {
                this.subscriptionsManager.add = this.transactionService.importConfirmedDeposits(lotDeposits).subscribe(
                    (res: HttpResponse<any>) =>
                        this.onSaveSuccess(lotUUIDs, SUCCESS_CONFIRMED));
            } else {
                this.subscriptionsManager.add = this.transactionService.importPendingDeposits(lotDeposits).subscribe(
                    (res: HttpResponse<any>) =>
                        this.onSaveSuccess(lotUUIDs, SUCCESS_PENDING));
            }
        }
    }

    private validate() {
        const errorMessage: string[] = [];
        this.validateData(this.importData.slice(1), errorMessage);

        if (errorMessage.length !== 0) {
            this.alertService.error('error.custom', { param: formatErrorList(errorMessage) });
            return false;
        }
        return true;
    }

    private validateData(importData: any[], errorMessage: string[]) {
        for (let i = 0; i < importData.length; i++) {
            const row = importData[i];
            // Lot UID
            if (!row[this.getLotUIDColumnIndex()]) {
                errorMessage.push(this.translateService.instant('inventoryManagerApp.lot.import.validation.lotUID')); // LOT ID
                break;
            }
            // Amount
            if (row[this.getAmountColumnIndex()] && (isNaN(row[this.getAmountColumnIndex()]) || row[this.getAmountColumnIndex()] <= 0) || !row[this.getAmountColumnIndex()]) {
                errorMessage.push(this.translateService.instant('inventoryManagerApp.lot.import.validation.amount.format'));
                break;
            }

            // Notes
            if (row[this.getNotesColumnIndex()] && row[this.getNotesColumnIndex()].length > 255) {
                errorMessage.push(this.translateService.instant('inventoryManagerApp.lot.import.validation.notes')); // Notes
                break;
            }
        }
    }

    private getLotUIDColumnIndex() {
        return this.header.indexOf(this.lotUIDMapping);
    }

    private getAmountColumnIndex() {
        return this.header.indexOf(this.amountMapping);
    }

    private getNotesColumnIndex() {
        return this.header.indexOf(this.notesMapping);
    }

    private onSaveSuccess(lotUUIDs, message) {
        this.alertService.success(message);
        this.eventManager.broadcast({ name: 'columnFiltersChanged', content: { filterBy: { lotUUIDs } } });
        this.modal.close('imported');
    }

}
